import React, { useEffect, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import TableReportNormalQT from "../Components/TableReportNormalQT";
import { APIServices } from "../helpers/APIServices";
import dayjs from "dayjs";
import LineChart from "../Components/LineChart";
import DateTime from "../Components/DateTime";
import Button from "@mui/material/Button";
import { UserContext } from "../Layout";
import config from "../config";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import SimpleBackdrop from "../Components/Backdrop";
import Download from "@mui/icons-material/Download";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { injectImageIfPlaceholderExists, getImageUrl } from "../helpers/common";

const headers = [
  {
    name: "No.",
  },
  {
    name: "create date",
  },
  {
    name: "Job No.",
  },
  {
    name: "answer",
    col: [
      {
        name: "No.1",
      },
      {
        name: "No.2",
      },
      {
        name: "No.3",
      },
    ],
  },
  {
    name: "inspector",
  },
  {
    name: "reviewer",
  },
];

const PerPathNormalQT = () => {
  const defaultDateTime = {
    start: `${dayjs().subtract(1, "d").format("YYYY-MM-DD")} 00:00:00`,
    end: `${dayjs().format("YYYY-MM-DD")} 23:59:59`,
  };
  const param = useParams();
  const [reportData, setReportData] = useState([]);
  const [startDate, setStartDate] = useState({
    error: false,
    value: defaultDateTime.start,
  });
  const [endDate, setEndDate] = useState({
    error: false,
    value: defaultDateTime.end,
  });
  const [loading, setLoading] = useState(false);

  const [checkDate, setCheckDate] = useState({
    error: false,
    message: "end date should more than start date",
  });
  const [selectedRows, setSelectedRows] = useState([]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelecteds = reportData.slice(0, 30).map((n) => n.id);
      setSelectedRows(newSelecteds);
      return;
    }
    setSelectedRows([]);
  };

  const handleSelectFirst30 = () => {
    const newSelecteds = reportData.slice(0, 30).map((n) => n.id);
    setSelectedRows(newSelecteds);
  };

  const handleSelectRow = (event, id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      if (selectedRows.length >= 30) {
        alert("สามารถเลือกข้อมูลเพื่อ Export ได้สูงสุด 30 รายการเท่านั้น");
        return;
      }
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelected);
  };

  const handleExportNormalQT = async () => {
    if (!part || reportData.length === 0) return;
    if (selectedRows.length === 0) {
      alert("กรุณาเปิด Checkbox เลือกข้อมูลอย่างน้อย 1 รายการก่อนทำการ Export");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/templates/Xbar R Chart_app.xlsx?v=' + new Date().getTime());
      let arrayBuffer = await response.arrayBuffer();

      if (part.part_image) {
        try {
          const fullImageUrl = getImageUrl(part.part_image);
          console.log("[Export NormalQT Debug] part_image full URL:", fullImageUrl);
          const imgRes = await fetch(fullImageUrl);
          const imgBlob = await imgRes.blob();
          const imgArrayBuffer = await imgBlob.arrayBuffer();
          arrayBuffer = await injectImageIfPlaceholderExists(arrayBuffer, imgArrayBuffer);
        } catch (imgError) {
          console.error("Could not override image for excel", imgError);
        }
      }

      const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
      const sheet = workbook.sheet(0);

      if (part.part_code) sheet.cell('C2').value(part.part_code);
      if (part.part_name) sheet.cell('O2').value(part.part_name);
      if (part.spec_max !== undefined) sheet.cell('D14').value(Number(part.spec_max));
      if (part.spec_min !== undefined) sheet.cell('F14').value(Number(part.spec_min));

      // Spec range text in merged A+B cells at row 19 and 31
      if (part.spec_min !== undefined && part.spec_max !== undefined) {
        const specText = `${Number(part.spec_min).toFixed(2)} - ${Number(part.spec_max).toFixed(2)}`;
        sheet.cell("A19").value(specText);
        sheet.cell("A31").value(specText);
      }

      // Customer → X2, Month → X3
      if (part.customer) sheet.cell("X2").value(part.customer);

      const selectedData = reportData.filter((item) => selectedRows.includes(item.id));

      if (selectedData.length > 0 && selectedData[0].date) {
        sheet.cell("X3").value(dayjs(selectedData[0].date, "DD/MM/YYYY").format("MMMM YYYY"));
      }
      
      for (let row = 40; row <= 42; row++) {
        for (let col = 6; col <= 35; col++) { 
           sheet.cell(row, col).value(null); 
        }
      }

      selectedData.forEach((item, idx) => {
         const colNumber = 6 + idx; 
         const actuals = (item.data && item.data.answer) ? item.data.answer : [];
         if (actuals[0] !== undefined && actuals[0] !== "null" && actuals[0] !== null) sheet.cell(40, colNumber).value(Number(actuals[0]));
         if (actuals[1] !== undefined && actuals[1] !== "null" && actuals[1] !== null) sheet.cell(41, colNumber).value(Number(actuals[1]));
         if (actuals[2] !== undefined && actuals[2] !== "null" && actuals[2] !== null) sheet.cell(42, colNumber).value(Number(actuals[2]));

         // Row 51: Job No, Row 52: Date, Row 53: Time
         if (item.jobNo) sheet.cell(51, colNumber).value(item.jobNo);
         if (item.date) sheet.cell(52, colNumber).value(item.date);
         if (item.time) sheet.cell(53, colNumber).value(item.time);
      });

      // Machine No → O3 (use first selected item's machine)
      if (selectedData.length > 0 && selectedData[0].machineNo) {
        sheet.cell("O3").value(selectedData[0].machineNo);
      }

      const blob = await workbook.outputAsync();
      saveAs(blob, `Report_Xbar_${part.part_code || 'Export'}.xlsx`);

    } catch (e) {
      console.error("Error in Download NormalQT Template:", e);
    }
    setLoading(false);
  };

  const [part, setPart] = useState({});
  const user = useContext(UserContext);
  let location = useLocation();
  let locationArr = location.pathname.split("/").reverse();

  const findOnePart = async (startDate, endDate) => {
    if (!startDate && !endDate) {
      startDate = defaultDateTime.start;
      endDate = defaultDateTime.end;
    }
    setLoading(true);
    try {
      const partId = locationArr[4];
      const [responseFromFindOnePart, responseFromFindOneQuestion, paper] =
        await Promise.all([
          APIServices.findOnePart(partId),
          APIServices.findOneQuestion(param.id),
          APIServices.getPaper(startDate, endDate, partId, user),
        ]);

      const resultFromFindOnePart = responseFromFindOnePart?.data;
      const resultFromFindOneQuestion = responseFromFindOneQuestion?.data;
      const parts = resultFromFindOnePart?.data;
      let questionData = {
        question_name: null,
        spec_min: null,
        spec_max: null,
      };

      if (parts?.attributes?.sub_categories?.data) {
        const subCats = parts.attributes.sub_categories.data;
        for (let i = 0; i < subCats.length; i++) {
          const subCatPerLoop = subCats[i];
          const questions = subCatPerLoop?.attributes?.questions?.data || [];
          const findQuestion = questions.find((qt) => {
            return qt.id === parseInt(param.id);
          });
          if (findQuestion) {
            questionData = {
              question_name: findQuestion.attributes?.question || null,
              spec_min: findQuestion.attributes?.spec_min || null,
              spec_max: findQuestion.attributes?.spec_max || null,
            };
            break;
          }
        }
      }

      if (!questionData.question_name && resultFromFindOneQuestion?.data) {
        const qAttr = resultFromFindOneQuestion.data.attributes;
        questionData = {
          question_name: qAttr?.question || null,
          spec_min: qAttr?.spec_min || null,
          spec_max: qAttr?.spec_max || null,
        };
      }

      if (parts) {
        let image = "";
        if (parts?.attributes?.image?.data?.attributes?.url) {
          image = parts.attributes.image.data.attributes.url;
        } else if (parts?.attributes?.image?.data?.[0]?.attributes?.url) {
          image = parts.attributes.image.data[0].attributes.url;
        } else if (parts?.attributes?.image?.url) {
          image = parts.attributes.image.url;
        } else if (
          Array.isArray(parts?.attributes?.image) &&
          parts.attributes.image[0]?.url
        ) {
          image = parts.attributes.image[0].url;
        }

        setPart({
          part_name: parts.attributes?.part_name || "",
          part_code: parts.attributes?.part_code || "",
          part_image: image,
          customer: parts.attributes?.customer || "",
          ...questionData,
        });

        const result = paper?.data;
        if (result?.data) {
          const papers = result.data;
          let report = [];
          papers.forEach((paper) => {
            try {
              let realData;
              const sub_categories = Array.isArray(
                paper?.attributes?.answer?.sub_categories,
              )
                ? paper.attributes.answer.sub_categories
                : [];

              let isSkip = false;
              sub_categories.forEach((sub_category) => {
                const questionsList = Array.isArray(sub_category?.questions)
                  ? sub_category.questions
                  : [];
                const realPaper = questionsList.find((p) => {
                  return p.questionId === parseInt(locationArr[0]);
                });
                if (realPaper) {
                  if (realPaper.skipQuestion) {
                    isSkip = true;
                    return;
                  }
                  realData = realPaper;
                }
              });
              if (isSkip) {
                return;
              }

              const inspector =
                paper.attributes?.inspector_id?.data?.attributes?.name || "-";
              const checker =
                paper.attributes?.checker_id?.data?.attributes?.name || "-";
              if (realData !== undefined) {
                report.push({
                  id: paper.id,
                  createAt: dayjs(paper.attributes?.createdAt).format(
                    "YYYY-MM-DD HH:mm:ss",
                  ),
                  data: realData,
                  jobNo: paper.attributes?.jobNumber || "",
                  date: paper.attributes?.date || "",
                  time: paper.attributes?.time || "",
                  machineNo: paper.attributes?.machineNumber || "",
                  inspector: inspector,
                  checker: checker,
                  statusDelete: paper.attributes?.delete,
                });
              }
            } catch (error) {
              console.error("Error processing paper:", error);
            }
          });
          setReportData(report);
        }
      }
    } catch (err) {
      console.error("Error in PerPathNormalQT findOnePart:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOnChangeStartDate = (date, time) => {
    if (!date || !time) {
      setStartDate((prev) => ({
        ...prev,
        value: undefined,
      }));
      return;
    }
    const startDate = dayjs(`${date} ${time}`).format("YYYY-MM-DD HH:mm:ss");
    setStartDate((prev) => ({
      ...prev,
      value: startDate,
      error: false,
    }));
  };

  const handleOnChangeEndDate = (date, time) => {
    if (!date || !time) {
      setEndDate((prev) => ({
        ...prev,
        value: undefined,
      }));
      return;
    }
    const endDate = dayjs(`${date} ${time}`).format("YYYY-MM-DD HH:mm:ss");
    setEndDate((prev) => ({
      ...prev,
      value: endDate,
      error: false,
    }));
  };

  const handleSubmitSearch = async (startDate, endDate) => {
    if (!startDate.value) {
      setStartDate((prev) => ({
        ...prev,
        error: true,
      }));
      return;
    }
    if (!endDate.value) {
      setEndDate((prev) => ({
        ...prev,
        error: true,
      }));
      return;
    }
    if (
      dayjs(endDate.value).isBefore(startDate.value, "minute") ||
      dayjs(endDate.value).isSame(startDate.value, "minute")
    ) {
      setCheckDate((prev) => ({
        ...prev,
        error: true,
      }));
      setTimeout(() => {
        setCheckDate((prev) => ({
          ...prev,
          error: false,
        }));
      }, 2000);
      return;
    }

    await findOnePart(startDate.value, endDate.value);
  };

  useEffect(() => {
    findOnePart();
  }, []);
  return (
    <div>
      {/* <pre>{JSON.stringify(part)}</pre> */}
      {/* <div className="bg-black w960 h-40 flex justify-center items-center">
      <div className="py-2 px-4 bg-white rounded-full hover:bg-yellow-300 transition">
        Sawanon Wattanasit
      </div>
      </div> */}
      {/* <div className={`lg:flex lg:flex-row p-2 justify-center flex flex-col`}> */}
      <SimpleBackdrop open={loading} setOpen={setLoading} />
      <div
        className={`bg-white rounded-md drop-shadow py-2 px-4 lg:mt-0 lg:basis-1/2 lg:m-1 flex flex-row`}
      >
        <div className={`basis-1/2 `}>
          {/* <div className="flex">
            <div className={`basis-1/2 text-gray-500`}>ID QT :</div>
            <div className={`basis-1/2 text-end`}>{part.part_name}</div>
          </div> */}
          <div className="flex">
            <div className={`basis-2/5 text-gray-500 `}>Part name :</div>
            <div className={`basis-3/5 text-end`}>{part.part_name}</div>
          </div>
          <div className="flex mt-2">
            <div className={`basis-2/5 text-gray-500`}>Part No. :</div>
            <div className={`basis-3/5 text-end`}>{part.part_code}</div>
          </div>
          <div className="flex mt-2">
            <div className={`basis-2/5 text-gray-500`}>Characteristic :</div>
            <div className={`basis-3/5 text-end`}>{part.question_name}</div>
          </div>
          {part.spec_min && part.spec_max && (
            <div className="flex mt-2">
              <div className={`basis-2/5 text-gray-500`}>Specification :</div>
              <div className={`basis-3/5 text-end`}>
                {part.spec_min && (
                  <span className={``}>{`(Min) ${part.spec_min.toFixed(
                    2
                  )}`}</span>
                )}
                {part.spec_max && (
                  <span className={`ml-4`}>{`(Max) ${part.spec_max.toFixed(
                    2
                  )}`}</span>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={`flex justify-center items-center basis-1/2 m-1`}>
          {part.part_image ? (
            <img src={getImageUrl(part.part_image)} className=" object-contain h-[300px]" alt="part" />
          ) : (
            <div
              style={{ width: "6rem", height: "6rem" }}
              className="bg-gray-100 flex items-center justify-center rounded-lg"
            >
              <NoPhotographyIcon />
            </div>
          )}
        </div>
      </div>
      {/* </div> */}
      <div>
        <div
          className={` md:basis-1/2 flex flex-row justify-center items-center mt-4 md:ml-2`}
        >
          {checkDate.error && (
            <div
              className={`bg-red-500 py-2 px-4 text-white absolute rounded-md -bottom-10`}
            >
              {checkDate.message}
            </div>
          )}
          <div className={`${startDate.error && `bg-red-300 rounded`}`}>
            <DateTime
              value={startDate.value}
              autoFocus={startDate.error}
              label={`Start date`}
              onChange={handleOnChangeStartDate}
            />
          </div>
          <div className={`${endDate.error && `bg-red-300 rounded`}`}>
            <DateTime
              value={endDate.value}
              autoFocus={endDate.error}
              label={`End date`}
              onChange={handleOnChangeEndDate}
            />
          </div>
          <div className="m-2 flex gap-2">
            <Button
              className="bg-orangeCustom hover:bg-orange-500"
              variant="contained"
              onClick={() => handleSubmitSearch(startDate, endDate)}
            >
              Search
            </Button>
            {reportData.length > 0 && (
              <Button
                variant="outlined"
                onClick={handleSelectFirst30}
              >
                Select 30
              </Button>
            )}
            <Button
              className="bg-orangeCustom hover:bg-orange-500"
              variant="contained"
              onClick={handleExportNormalQT}
            >
              <Download sx={{ mr: 1 }} /> Export
            </Button>
          </div>
        </div>
        <div
          className={`lg:mt-2 mt-2 p-2  flex justify-center items-center lg:flex lg:justify-center lg:items-center `}
        >
          {reportData.length > 0 ? (
            <TableReportNormalQT
              className={`mt-4`}
              data={reportData}
              headers={headers}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
            />
          ) : (
            <div
              className={`text-2xl flex justify-center items-center lg:flex lg:justify-center lg:items-center `}
            >
              No data
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerPathNormalQT;
